import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/lib/api';
import type { ApiError } from '@/lib/api';
import type { CreateSurveyResponseDto, SurveyState } from '@/types/survey';

import { ResponseType } from '@/types/survey';

const initialState: SurveyState = {
  surveys: [],
  currentSurvey: null,
  surveyResponses: [],
  loading: false,
  error: null,
  formData: {},
  progress: {
    currentQuestion: 0,
    totalQuestions: 0,
    completedQuestions: 0,
    isCompleted: false,
  },
  isSubmitting: false,
};

// Async thunks
export const fetchPublishedSurveys = createAsyncThunk(
  'survey/fetchPublishedSurveys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/surveys/published');
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch surveys'
      );
    }
  }
);

export const fetchSurveyById = createAsyncThunk(
  'survey/fetchSurveyById',
  async (surveyId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/surveys/public/${surveyId}`);
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch survey'
      );
    }
  }
);

export const submitSurveyResponse = createAsyncThunk(
  'survey/submitSurveyResponse',
  async (data: CreateSurveyResponseDto, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/surveys/${data.surveyId}/respond`,
        data
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to submit survey response'
      );
    }
  }
);

export const fetchSurveyByOrganization = createAsyncThunk(
  'survey/fetchSurveyByOrganization',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/surveys/organization/${organizationId}`);
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to fetch organization surveys'
      );
    }
  }
);

const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    setCurrentSurvey: (state, action) => {
      state.currentSurvey = action.payload;
      if (action.payload) {
        state.progress.totalQuestions = action.payload.questions.length;
        state.progress.currentQuestion = 0;
        state.progress.completedQuestions = 0;
        state.progress.isCompleted = false;
        state.formData = {};
      }
    },
    updateFormData: (state, action) => {
      const { questionId, answer } = action.payload;
      state.formData[questionId] = answer;

      // Update progress - count only questions that are actually answered with valid types
      const answeredQuestions = Object.values(state.formData).filter((data) => {
        if (!data || !data.type) {
          return false;
        }

        // Check if the question has been properly answered based on its type
        if (
          data.type === ResponseType.TEXT &&
          (!data.textAnswer || !data.textAnswer.trim())
        ) {
          return false;
        }
        if (
          data.type === ResponseType.SCALE &&
          data.scaleAnswer === undefined
        ) {
          return false;
        }
        if (
          (data.type === ResponseType.SINGLE_CHOICE ||
            data.type === ResponseType.MULTIPLE_CHOICE) &&
          (!data.choiceAnswers || data.choiceAnswers.length === 0)
        ) {
          return false;
        }

        return true;
      }).length;

      state.progress.completedQuestions = answeredQuestions;
      state.progress.isCompleted =
        answeredQuestions === state.progress.totalQuestions;
    },
    setCurrentQuestion: (state, action) => {
      state.progress.currentQuestion = action.payload;
    },
    nextQuestion: (state) => {
      if (state.progress.currentQuestion < state.progress.totalQuestions - 1) {
        state.progress.currentQuestion += 1;
      }
    },
    previousQuestion: (state) => {
      if (state.progress.currentQuestion > 0) {
        state.progress.currentQuestion -= 1;
      }
    },
    goToQuestion: (state, action) => {
      const questionIndex = action.payload;
      if (questionIndex >= 0 && questionIndex < state.progress.totalQuestions) {
        state.progress.currentQuestion = questionIndex;
      }
    },
    resetSurveyForm: (state) => {
      state.formData = {};
      state.progress = {
        currentQuestion: 0,
        totalQuestions: 0,
        completedQuestions: 0,
        isCompleted: false,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSurveyData: (state) => {
      state.currentSurvey = null;
      state.formData = {};
      state.progress = {
        currentQuestion: 0,
        totalQuestions: 0,
        completedQuestions: 0,
        isCompleted: false,
      };
    },
  },
  extraReducers: (builder) => {
    // fetchPublishedSurveys
    builder
      .addCase(fetchPublishedSurveys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublishedSurveys.fulfilled, (state, action) => {
        state.loading = false;
        state.surveys = action.payload;
      })
      .addCase(fetchPublishedSurveys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchSurveyById
    builder
      .addCase(fetchSurveyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSurveyById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSurvey = action.payload;
        state.progress.totalQuestions = action.payload.questions.length;
        state.progress.currentQuestion = 0;
        state.progress.completedQuestions = 0;
        state.progress.isCompleted = false;

        // Initialize form data with default values for all questions
        state.formData = {};
        action.payload.questions.forEach(
          (question: { _id: string }, index: number) => {
            const questionId = question._id || `question-${index}`;
            state.formData[questionId] = {
              type: undefined,
              textAnswer: '',
              scaleAnswer: undefined,
              choiceAnswers: [],
            };
          }
        );
      })
      .addCase(fetchSurveyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // submitSurveyResponse
    builder
      .addCase(submitSurveyResponse.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitSurveyResponse.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.surveyResponses.push(action.payload);
        state.progress.isCompleted = true;
      })
      .addCase(submitSurveyResponse.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });

    // fetchSurveyByOrganization
    builder
      .addCase(fetchSurveyByOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSurveyByOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.surveys = action.payload;
      })
      .addCase(fetchSurveyByOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentSurvey,
  updateFormData,
  setCurrentQuestion,
  nextQuestion,
  previousQuestion,
  goToQuestion,
  resetSurveyForm,
  clearError,
  clearSurveyData,
} = surveySlice.actions;

export default surveySlice.reducer;
